import React, { createContext, ReactNode, useContext, useState } from "react";
import { apiModulo } from "../services/api";
import { DevedorCadastro } from "../types/devedor-interface";
import { useMutation } from "@tanstack/react-query";
import { ConsultaDebitoResponse, DebitoCredorProps } from "../types/divida-interface";
import { useContrato } from "./ContratoContext";
import { useModal } from "../hooks/ModalContext";

interface DividasContextProviderProps {
  children: ReactNode;
}

interface Contratante {
  id_cont_credor: number;
  url_ws: string;
  cod_group: string;
}

interface CredorProps {
  id_credor: number;
  name_credor: string;
  logo: string;
  credor_crm: number;
  contratante: Contratante[];
}

interface Divida {
  id_debt: string;
  credor: CredorProps[];
}


export interface DevedorProps {
  id_debtor: string;
  name: string;
  cpf_cnpj: string;
  id_chat?: string;
  divida: Divida[];
}

export interface Credor {
  logo: string;
  nome: string;
  credor_crm: number;
  url_ws: string;
  parcelasIndividuaisSequenciais?: boolean;
  codcontratante?: number;
}

interface DividasContextProps {
  getDividas({
    cpf_cnpj,
    codcredor,
  }: {
    cpf_cnpj: string;
    codcredor: number;
    usuario?: string
  }): Promise<void>;
  getDividasGazin({ app, cpf_cnpj, id, }: {
    app?: string;
    cpf_cnpj: string;
    id?: number;
    usuario?: string
  }): Promise<void>
  devedor: ConsultaDebitoResponse;
  credores: Credor[];
  setDevedor: (value: ConsultaDebitoResponse) => void;
  getDevedorCadastro(
    url: string,
    cpf_cnpj: string,
    codcredor: number,
    app?: string,
    usuario?: string
  ): Promise<void>;
  devedorCadastro: DevedorCadastro;
  getSelectedCredor(credor: Credor): void;
  clearDividas(): void;
  clearDevedorCadastro(): void;
  selectedCredor: Credor;
  setCredores: React.Dispatch<React.SetStateAction<Credor[]>>
}

const DividasContext = createContext<DividasContextProps>(
  {} as DividasContextProps
);

export const DividasProvider = ({ children }: DividasContextProviderProps) => {
  const { getContracts } = useContrato()

  const [devedor, setDevedor] = useState<ConsultaDebitoResponse>({} as ConsultaDebitoResponse);
  const [credores, setCredores] = useState<Credor[]>([]);
  const [devedorCadastro, setDevedorCadastro] = useState<DevedorCadastro>(
    {} as DevedorCadastro
  );
  const [selectedCredor, setSelectedCredor] = useState<Credor>({} as Credor);
  const { setModal } = useModal()

  const mutationGetDividas = useMutation({
    mutationFn: ({
      url,
      codcredor,
      cpf_cnpj,
      app,
      usuario
    }: {
      url: string;
      cpf_cnpj: string;
      codcredor: number;
      app?: string;
      usuario?: string;
    }) => {
      return apiModulo.post(
        "/debts/debtor",
        { url, app: app || 'ACIONA_WEB' },
        { params: { cpfCnpj: cpf_cnpj, codcredor, usuario } }
      );
    },
    mutationKey: ["form"],
    onError: () => setModal('Ops! Erro ao localizar o cliente. Tente novamente mais tarde!', 'Erro', false, true)
  });

  const mutationGetCredores = useMutation({
    mutationFn: ({
      cpf_cnpj,
      usuario
    }: {
      cpf_cnpj: string;
      usuario?: string
    }) => {
      return apiModulo.get("/debts/credor", {
        params: { cpf_cnpj: cpf_cnpj, usuario },
      })
    },
    mutationKey: ["form"],
    onError: () => {
      setDevedor({ name: 'Error' } as ConsultaDebitoResponse)
    }
  });

  function clearDevedorCadastro() {
    setDevedorCadastro({} as DevedorCadastro);
    setSelectedCredor({} as Credor);
  }

  async function getDividasGazin({
    app,
    cpf_cnpj,
    id,
    usuario
  }: {
    app?: string;
    cpf_cnpj: string;
    id: number;
    usuario?: string
  }) {
    const appWS = app ? app : "ACIONA_OPE"


    let credoresComInfo = [] as Credor[];
    let credor = [] as string[]

    const cred = {
      agrupador: 434,
      codcontratante: 1,
      logomarca: "https://arquivos-jprosystem.s3.us-east-2.amazonaws.com/imagens/gazin.png",
      nomcontratante: "Gazin",
      url: "http://malta-ws.gazin.com.br:8888/NegociacaoAppHomologacao/MaltaAssessoriaSrv434.dll/wsdl/IServico",
      // url: "http://malta-ws.gazin.com.br:9999/NegociacaoWeb/WSGestaoCobrancaDSB.dll/wsdl/IServicoCobranca",

    }

    const contratos = await getContracts({ cpf_cnpj, credor: cred.agrupador, url: cred.url, app: appWS, isOperador: true, usuario })
    credor = contratos.contratos.map(cont => cont.codcredor)
    credor = credor.filter((value, index) => {
      return credor.indexOf(value) === index;
    });
    const credorAgrupador = credor.map(credor => {
      return {
        credor_crm: Number(credor),
        logo: cred.logomarca,
        url_ws: cred.url,
        nome: cred.nomcontratante,
        codcontratante: cred.codcontratante,
      }
    })
    credoresComInfo = [...credorAgrupador]


    const uniqueIds = new Set();
    credoresComInfo = credoresComInfo.filter(item => {
      if (!uniqueIds.has(item.credor_crm)) {
        uniqueIds.add(item.credor_crm);
        return true;
      }
      return false;
    });

    const { data: dataCadastro } = await mutationGetDividas.mutateAsync({
      url: credoresComInfo[0].url_ws,
      cpf_cnpj: cpf_cnpj,
      codcredor: credoresComInfo[0].credor_crm,
      app: app || 'ACIONA_OPE',
      usuario
    });

    setDevedor({
      id_debtor: String(id),
      cpf_cnpj,
      name: dataCadastro?.devedor.nomclien,
      divida: credoresComInfo.map(credor => {
        return {
          agrupador: credor.credor_crm,
          codcontratante: Number(credor.codcontratante),
          logomarca: credor.logo,
          nomcontratante: credor.nome,
          url: credor.url_ws
        }
      })
    });
    setDevedorCadastro(dataCadastro?.devedor);
    setCredores([...credoresComInfo]);
    return;
  }

  function clearDividas() {
    setDevedorCadastro({} as DevedorCadastro);
    setSelectedCredor({} as Credor);
    setCredores([]);
    setDevedor({} as ConsultaDebitoResponse);
  }

  function getSelectedCredor(credor: Credor) {
    setSelectedCredor(credor);
  }

  async function getDividas({
    cpf_cnpj,
    codcredor,
    usuario
  }: {
    cpf_cnpj: string;
    codcredor: number;
    usuario?: string
  }) {

    const { data } = await mutationGetCredores.mutateAsync({ cpf_cnpj, usuario })

    setDevedor({ ...data?.devedor, cpf_cnpj });
    if (data?.devedor === null) {
      setCredores([]);
      setDevedorCadastro({} as DevedorCadastro);
      return;
    }


    let credoresComInfo = [] as Credor[];
    let credor = [] as string[]
    const credores = data?.devedor.divida as DebitoCredorProps[];

    //Colocar um get contrato aqui para fazer a divisão de credores. 
    await Promise.all(credores?.map(async c => {
      const contratos = await getContracts({ cpf_cnpj, credor: c.agrupador, url: c.url })
      credor = contratos.contratos.map(cont => cont.codcredor)
      credor = credor.filter((value, index) => {
        return credor.indexOf(value) === index;
      });
      const credorAgrupador = credor.map(credor => {
        return {
          credor_crm: Number(credor),
          logo: c.logomarca,
          url_ws: c.url,
          nome: c.nomcontratante,
          codcontratante: c.codcontratante,
          // parcelasIndividuaisSequenciais: c.parcelasIndividuaisSequenciais,
        }
      })
      credoresComInfo = [...credorAgrupador]
    }))

    const uniqueIds = new Set();
    credoresComInfo = credoresComInfo.filter(item => {
      if (!uniqueIds.has(item.credor_crm)) {
        uniqueIds.add(item.credor_crm);
        return true;
      }
      return false;
    });

    setCredores([...credoresComInfo]);

    const { data: dataCadastro } = await mutationGetDividas.mutateAsync({
      url: credoresComInfo[0].url_ws,
      cpf_cnpj: cpf_cnpj,
      codcredor: credoresComInfo[0].credor_crm,
      usuario
    });

    setDevedorCadastro(dataCadastro?.devedor);

    return;
  }

  async function getDevedorCadastro(
    url: string,
    cpf_cnpj: string,
    codcredor: number,
    app?: string,
    usuario?: string
  ) {
    const { data: dataCadastro } = await mutationGetDividas.mutateAsync({
      url,
      cpf_cnpj: cpf_cnpj.replace(/\D/g, ''),
      codcredor,
      app,
      usuario
    });
    setDevedorCadastro(dataCadastro?.devedor);
    return;
  }
  return (
    <DividasContext.Provider
      value={{
        getDividas,
        devedor,
        setDevedor,
        credores,
        getDividasGazin,
        getDevedorCadastro,
        devedorCadastro,
        getSelectedCredor,
        selectedCredor,
        clearDividas,
        clearDevedorCadastro,
        setCredores
      }}
    >
      {children}
    </DividasContext.Provider>
  );
};

export const useDividas = () => useContext(DividasContext);
